import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { BellRing, Play, Sparkles } from 'lucide-react';
import { format, differenceInMinutes } from 'date-fns';
import { ko } from 'date-fns/locale';
import { toast } from 'sonner';
import SafeImg from '@/components/SafeImg';
import { getBroadcastPhase, getWatchButtonLabel } from '@/lib/broadcastStatus';

const PLATFORM_LABEL = {
  tiktok: '틱톡', instagram: '인스타그램', youtube: '유튜브',
  naver: '네이버 쇼핑라이브', kakao: '카카오 쇼핑라이브', grip: '그립', clickmate: '클릭메이트',
  others: '기타',
};
const CATEGORY_LABEL = {
  fashion: '패션', beauty: '뷰티', food: '식품', pet: '펫', kids: '키즈', living: '리빙',
  electronics: '전자제품', home: '홈', health: '헬스', sports: '스포츠', others: '기타',
};

/**
 * Vertical broadcast row.
 *
 * rowLinkMode:
 *   'seller'   — row click → /seller/{seller_id}   (Home)
 *   'schedule' — row click → /schedule?date=…      (Schedule page)
 *
 * The watch button always dispatches by time:
 *   before  → /broadcast/{id}/waiting
 *   live    → external stream_url (or waiting if missing)
 *   ended   → /broadcast/{id}/ended
 */
const ScheduleListItem = ({ live, showAd = false, rowLinkMode = 'seller' }) => {
  const navigate = useNavigate();
  const start = new Date(live.scheduled_at);
  const dateIso = start.toISOString().slice(0, 10);
  const image = live.home_schedule_image_url || live.promotion_image || live.thumbnail;
  const title = live.home_schedule_title || live.title;
  const promoted = showAd && (live.is_promoted || live.is_paid_promotion);
  const phase = getBroadcastPhase(live);
  const watchLabel = getWatchButtonLabel(phase);

  const minsUntil = differenceInMinutes(start, new Date());
  let countdown = null;
  if (phase === 'live') countdown = 'LIVE';
  else if (phase === 'ended') countdown = '종료';
  else if (minsUntil <= 5) countdown = '곧 시작';
  else if (minsUntil <= 60) countdown = `${minsUntil}분 후`;
  else if (minsUntil <= 60 * 24) countdown = `${Math.round(minsUntil / 60)}시간 후`;

  const handleRowClick = () => {
    // Round 3 §15 (2026-09-10) · External LIVE card body click → LIVEMOA
    // Watch Hub (통합 시청/탐색 화면).  Home / LIVE-only / Schedule 세
    // 진입점 모두 동일 Watch Hub 사용 · UX 일관.  기존 external icon
    // 아이콘 클릭 (`schedule-watch-*` 버튼) 은 그대로 원본 URL 새 탭 유지
    // (§16 두 동작 구분).  Internal(LIVEMOA) LIVE 는 기존 seller-profile
    // 라우팅 유지 (§17 UPCOMING/ENDED 및 native 는 별도 STEP).
    if (rowLinkMode === 'schedule' && live.external_id && (
        live.platform === 'naver_shopping_live' ||
        live.platform === 'kakao_shopping_live')) {
      navigate(`/live/hub/${live.external_id}`);
      return;
    }
    if (rowLinkMode === 'schedule') {
      navigate(`/schedule?date=${dateIso}&broadcast=${encodeURIComponent(live.schedule_id)}`);
    } else {
      navigate(`/seller/${live.seller_id}`);
    }
  };

  const handleWatch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // STEP 8-HOME-SCHEDULE-LIVE-CLICK-REAL-FIX · LIVE 라우팅 우선순위 강제
    // 1) status LIVE && streaming_session_id → /live-view/{sid} (SPA navigate)
    // 2) status LIVE && stream_url startsWith('/live-view/') → 그 URL (SPA navigate)
    // 3) 외부 LIVE URL → 기존 window.open
    // 4) 그 외 → 기존 waiting/ended 라우팅
    if (phase === 'live') {
      const sessId = live.streaming_session_id;
      const internal = (typeof live.stream_url === 'string' && live.stream_url.startsWith('/live-view/'))
        ? live.stream_url : null;
      if (sessId) { navigate(`/live-view/${sessId}`); return; }
      if (internal) { navigate(internal); return; }
      if (live.stream_url) { window.open(live.stream_url, '_blank', 'noopener,noreferrer'); return; }
      navigate(`/broadcast/${live.schedule_id}/waiting`); return;
    }
    if (phase === 'before') {
      navigate(`/broadcast/${live.schedule_id}/waiting`);
    } else {
      navigate(`/broadcast/${live.schedule_id}/ended`);
    }
  };

  const handleNotify = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toast.success('알림 신청이 완료되었습니다.');
  };

  const rowAriaLabel = rowLinkMode === 'schedule'
    ? `${title} 편성표에서 보기`
    : `${live.seller_name} 셀러 프로필로 이동`;

  const watchColor = phase === 'ended'
    ? 'bg-gray-400 cursor-default'
    : phase === 'live'
      ? 'bg-[#FF3B30] hover:bg-[#D32F2F]'
      : 'bg-black hover:bg-[#FF3B30] dark:bg-white dark:text-black dark:hover:bg-[#FF3B30] dark:hover:text-white';

  return (
    <div
      id={`broadcast-${live.schedule_id}`}
      onClick={handleRowClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(); } }}
      role="link"
      tabIndex={0}
      aria-label={rowAriaLabel}
      data-testid={`schedule-row-${live.schedule_id}`}
      data-row-link-mode={rowLinkMode}
      className="group flex items-stretch gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4 bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-xl hover:border-[#FF3B30]/40 hover:shadow-sm transition-all duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF3B30]"
    >
      {/* Time column */}
      <div className="flex flex-col items-center justify-center shrink-0 w-14 sm:w-20 py-1 border-r border-dashed border-black/10 dark:border-white/10 pr-3 sm:pr-4">
        <span className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 leading-none">
          {format(start, 'M.d', { locale: ko })}
        </span>
        <span className="mt-1 text-lg sm:text-xl font-black tracking-tight text-black dark:text-white leading-none" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {format(start, 'HH:mm')}
        </span>
        <span className="mt-1 text-[10px] sm:text-[11px] text-gray-400 dark:text-gray-500 leading-none">
          {format(start, 'EEE', { locale: ko })}
        </span>
        {countdown && (
          <span className={`mt-1 text-[10px] sm:text-[11px] font-bold leading-none ${phase === 'live' ? 'text-[#FF3B30]' : phase === 'ended' ? 'text-gray-400' : 'text-[#FF3B30]'}`} data-testid="countdown-badge">
            {countdown}
          </span>
        )}
      </div>

      {/* Thumbnail */}
      <div className="relative shrink-0 w-16 h-16 sm:w-24 sm:h-24 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
        <SafeImg
          src={image}
          kind="broadcast"
          alt={title}
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-200 ${phase === 'ended' ? 'grayscale opacity-70' : ''}`}
        />
        {promoted && (
          <span className="absolute top-1 left-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#FF3B30] text-white text-[9px] font-black" data-testid="ad-badge">
            <Sparkles size={8} /> AD
          </span>
        )}
      </div>

      {/* Title + meta */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <p
          className="font-bold text-sm sm:text-base tracking-tight text-black dark:text-white line-clamp-2 leading-snug"
          style={{ fontFamily: 'Manrope, sans-serif' }}
          data-testid="schedule-row-title"
        >
          {title}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] sm:text-xs text-gray-600 dark:text-gray-400">
          <Link
            to={`/seller/${live.seller_id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-gray-800 dark:text-gray-200 truncate max-w-[8rem] hover:text-[#FF3B30] hover:underline"
            data-testid="schedule-row-seller"
          >
            {live.seller_name}
          </Link>
          <span className="text-gray-300 dark:text-gray-600">·</span>
          {(() => {
            // Round 2 §1 (2026-09-10) — compact platform chip w/ brand color.
            const BRAND = {
              naver_shopping_live: { bg: '#03C75A', fg: '#fff', label: '네이버' },
              kakao_shopping_live: { bg: '#FEE500', fg: '#1a1a1a', label: '카카오' },
              naver:               { bg: '#03C75A', fg: '#fff', label: '네이버' },
              kakao:               { bg: '#FEE500', fg: '#1a1a1a', label: '카카오' },
            };
            const b = BRAND[live.platform];
            if (b) {
              return (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap leading-none"
                  style={{ background: b.bg, color: b.fg }}
                  data-testid={`schedule-row-platform-${live.platform}`}
                >
                  {b.label}
                </span>
              );
            }
            return (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-black/5 dark:bg-white/10 font-medium text-[10px] whitespace-nowrap"
                    data-testid={`schedule-row-platform-${live.platform}`}>
                {PLATFORM_LABEL[live.platform] || live.platform}
              </span>
            );
          })()}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#FF3B30]/10 text-[#FF3B30] font-medium">
            {CATEGORY_LABEL[live.category] || live.category}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 sm:gap-2 shrink-0 justify-center">
        <button
          type="button"
          onClick={handleNotify}
          className="inline-flex items-center justify-center gap-1 px-2.5 sm:px-3 py-1.5 sm:py-2 border border-black/10 dark:border-white/10 rounded-full text-[11px] sm:text-xs font-bold hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors"
          data-testid={`schedule-notify-${live.schedule_id}`}
          aria-label="알림 받기"
        >
          <BellRing size={12} /> <span className="hidden sm:inline">알림</span>
        </button>
        <button
          type="button"
          onClick={handleWatch}
          className={`inline-flex items-center justify-center gap-1 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full text-[11px] sm:text-xs font-bold text-white transition-colors ${watchColor}`}
          data-testid={`schedule-watch-${live.schedule_id}`}
          data-broadcast-phase={phase}
          aria-label={watchLabel}
        >
          <Play size={12} fill="currentColor" /> <span className="hidden sm:inline">{watchLabel}</span>
        </button>
      </div>
    </div>
  );
};

export default ScheduleListItem;
